import { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useWishlistContext } from '../contexts/WishlistContext';
import { AuthContext } from '../contexts/AuthContext';

/**
 * WishlistButton Component
 *
 * Displays a heart icon that toggles product in/out of wishlist
 *
 * @param {number} productId - Product ID to wishlist
 * @param {string} className - Additional CSS classes
 * @param {boolean} showText - Show "Add to Wishlist" text (default: false)
 * @param {string} iconSize - Icon size class (default: 'fa-lg')
 */
const WishlistButton = ({
  productId,
  className = '',
  showText = false,
  iconSize = 'fa-lg'
}) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const {
    isInWishlist,
    addToWishlist,
    removeFromWishlist,
    loading
  } = useWishlistContext();

  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Check wishlist status on mount and when wishlist changes
  useEffect(() => {
    if (isAuthenticated && productId) {
      setIsWishlisted(isInWishlist(productId));
    }
  }, [isAuthenticated, productId, isInWishlist]);

  /**
   * Handle wishlist toggle
   */
  const handleToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent card click when on product listing

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
      toast.info('Please log in to add items to your wishlist');
      // Redirect to login with return URL
      navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      return;
    }

    setIsProcessing(true);

    try {
      if (isWishlisted) {
        // Remove from wishlist
        const result = await removeFromWishlist(productId);
        if (result.success) {
          setIsWishlisted(false);
          toast.success('Removed from wishlist');
        } else {
          throw new Error(result.error);
        }
      } else {
        // Add to wishlist
        const result = await addToWishlist(productId);
        if (result.success) {
          setIsWishlisted(true);
          toast.success('Added to wishlist');
        } else {
          throw new Error(result.error);
        }
      }
    } catch (error) {
      console.error('Wishlist toggle error:', error);
      toast.error(error.message || 'Failed to update wishlist');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <button
      type="button"
      className={`wishlist-button ${isWishlisted ? 'active' : ''} ${className}`}
      onClick={handleToggle}
      disabled={isProcessing || loading}
      aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
      title={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
    >
      <i
        className={`
          ${isWishlisted ? 'fas' : 'far'}
          fa-heart
          ${iconSize}
          ${isProcessing ? 'fa-pulse' : ''}
        `}
      />
      {showText && (
        <span className="wishlist-button-text ms-2">
          {isWishlisted ? 'Saved' : 'Save'}
        </span>
      )}
    </button>
  );
};

export default WishlistButton;
